import * as THREE from "three";
import React, { useState, useRef, useEffect, memo } from "react";
import { useThree } from "@react-three/fiber";
import { Text } from '@react-three/drei';
import { PointsClick } from '../shape/PointClick';
import { TubeCurve } from "../shape/Spline";
import { ToCoord3D } from "../shape/Intersect";
import { MPointMoveAny } from "./mPointMove";

const MMeasure = memo(({angle, color, colorHigh, colorSub}) => {
  const refMesh = useRef();
  const { camera, mouse } = useThree();
  const [dist, setDist] = useState(0);
  const [clickPoints, setClickPoints] = useState([]);
  const [crossPoints, setCrossPoints] = useState([]);
  const [textPosition, setTextPosition] = useState([0, 0, 0]);
  const [textRotation, setTextRotation] = useState([0, 0, 0]);
  const temp = [];
  const offset = 300;
  const [offsetVec, setOffsetVec] = useState(new THREE.Vector3(0,0,offset));
  const [meshPlane, setMeshPlane] = useState(null);
  
  const [plane, setPlane] = useState('xy');

  useEffect(() => {
    if (angle === 1) {
      setPlane('xy');
    } else if (angle === 2) {
      setPlane('-xy');
    } else if (angle === 3) {
      setPlane('xz');
    } else if (angle === 4) {
      setPlane('-xz');
    } else if (angle === 5) {
      setPlane('-yz');
    } else if (angle === 6) {
      setPlane('yz');
    }
  }, []);

  useEffect(() => {
    if (refMesh.current) {
      var vecPlane = null;
      if (angle === 1) { // front
        setPlane('xy');
        vecPlane = new THREE.Vector3(0,0,offset)
        setOffsetVec(vecPlane);
        setTextRotation([0, 0, 0]);
        refMesh.current.rotation.x = 0;
        refMesh.current.rotation.y = 0;
      } else if (angle === 2) { // 2: back
        setPlane('-xy');
        vecPlane = new THREE.Vector3(0,0,-offset)
        setOffsetVec(vecPlane);
        setTextRotation([0, Math.PI, 0]);
        refMesh.current.rotation.y = Math.PI;
      } else if (angle === 3) { // 3: top
        setPlane('xz');
        vecPlane = new THREE.Vector3(0,offset,0)
        setOffsetVec(vecPlane);
        setTextRotation([-Math.PI/2, 0, 0]);
        refMesh.current.rotation.x = -Math.PI/2;
        refMesh.current.rotation.y = 0;
      } else if (angle === 4) { // 4: bottom
        setPlane('-xz');
        vecPlane = new THREE.Vector3(0,-offset,0)
        setOffsetVec(vecPlane);
        setTextRotation([Math.PI/2, 0, 0]);
        refMesh.current.rotation.x = Math.PI/2;
        refMesh.current.rotation.y = 0;
      } else if (angle === 5) { // 5: inside
        setPlane('-yz');
        vecPlane = new THREE.Vector3(-offset,0,0)
        setOffsetVec(vecPlane);
        setTextRotation([0, -Math.PI/2, 0]);
        refMesh.current.rotation.x = 0;
        refMesh.current.rotation.y = -Math.PI/2;
      } else if (angle === 6) { // 6: outside
        setPlane('yz');
        vecPlane = new THREE.Vector3(offset,0,0)
        setOffsetVec(vecPlane);
        setTextRotation([0, Math.PI/2, 0]);
        refMesh.current.rotation.x = 0;
        refMesh.current.rotation.y = Math.PI/2;
      }

      setMeshPlane(new THREE.Plane(vecPlane, 0));
    }
  }, [angle]);

  const onClick = () => {

    //var pt = new THREE.Vector3(mouse.x*100, mouse.y*100, 0);
    var pt = ToCoord3D(refMesh.current, camera, mouse);
    pt = pt.add(offsetVec);

    if (clickPoints.length === 2) {
      temp.length = 0; // clear
      clickPoints.length = 0; // clear
    }
    temp.push(pt);
    //setTemp(temp);

    if (clickPoints.length === 0) {
      setClickPoints(temp);
    }

    if (clickPoints.length === 1) {
      setClickPoints([clickPoints[0], temp[0]]);
      setDist(clickPoints[0].distanceTo(temp[0]));
 
      setTextPosition(clickPoints[0].clone().add(temp[0].clone()).divideScalar(2));

      // Middle point of measure line
      const middlePoint = clickPoints[0].clone().add(temp[0].clone()).divideScalar(2);
      
      const vec = clickPoints[0].clone().sub(middlePoint);
      var vec2;
      // 1: front, 2: back, 3: top, 4: bottom, 5: inside, 6: outside
      if (angle === 1 || angle === 2) {
        vec2 = new THREE.Vector3(0, 0, 1);
      } else if (angle === 3 || angle === 4) {
        vec2 = new THREE.Vector3(0, 1, 0);
      } else if (angle === 5 || angle === 6) {
        vec2 = new THREE.Vector3(1, 0, 0);
      }
      // Cross point of measure line
      const cross = vec.cross(vec2);

      setCrossPoints([
        middlePoint.clone().add(cross.clone().normalize().multiplyScalar(20)),
        middlePoint.clone().add(cross.clone().normalize().multiplyScalar(-20))
      ])
    }
  }

  return (
    <>
      <MPointMoveAny color={colorHigh} plane={plane}/>
      <PointsClick vpoints={clickPoints} color={colorHigh} size={5} callback={null}/>
      
      { clickPoints.length === 2 ? <TubeCurve vpoints={clickPoints} radius={0.5} color={colorHigh}/> : null}
      { clickPoints.length === 2 ? <TubeCurve vpoints={crossPoints} radius={0.5} color={colorHigh}/> : null}
      { clickPoints.length === 2 ? 
        <Text
          position={textPosition}
          rotation={textRotation}
          color={colorHigh}
          fontSize={20}
          maxWidth={200} // option
          textAlign='left' // option
          anchorX='left' // option
          anchorY='middle' // option
        >
          {Math.round(dist*10)/10} mm
        </Text>
      : null}
      <mesh ref={refMesh} onClick={() => onClick()}>
        <planeBufferGeometry attach="geometry" args={[10000, 10000]} />
        <meshStandardMaterial attach="material" transparent opacity={0.2} side={THREE.DoubleSide} color="orange" />
      </mesh>
    </>
  )
});

export default MMeasure;